(() => {
  'use strict';

  // Buy now has its own path, including when the cart drawer is unavailable.
  // The native product form also has return_to for the no-JavaScript fallback.
  // A confirmed buy-now request may be resumed after returning from checkout.
  // The receipt is tab-scoped and never authorizes changing existing cart lines.
  // BEGIN BUY-NOW RECEIPT HELPERS
  const buyNowReceiptKey = 'nuitigo:buy-now-receipt:v1';
  let buyNowReceiptMemory = null;
  function stableValue(value) {
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]));
    }
    return value;
  }
  function selectionSignature(data) {
    const entries = [];
    for (const [key, value] of data.entries()) {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan' || /^properties\[.+\]$/.test(key)) {
        if (typeof value !== 'string') return null;
        entries.push([key, value]);
      }
    }
    entries.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
    if (!entries.some(([key, value]) => key === 'id' && /^\d+$/.test(value))) return null;
    const quantity = Number(data.get('quantity') || '1');
    if (!Number.isSafeInteger(quantity) || quantity < 1) return null;
    return JSON.stringify(entries);
  }
  function cartSignature(cart) {
    if (!cart || typeof cart.token !== 'string' || !cart.token || !Array.isArray(cart.items) || !cart.items.length) return null;
    if (cart.items.some((item) => !item || typeof item.key !== 'string' || !item.key || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) return null;
    // Preserve other products, formats, quantities, prices, discounts and cart notes.
    // Ignore only presentation metadata such as image URLs and product descriptions.
    const items = cart.items.map((item) => ({
      key: item.key,
      variant: String(item.variant_id || item.id),
      quantity: item.quantity,
      properties: item.properties || {},
      sellingPlan: item.selling_plan_allocation || null,
      originalPrice: item.original_price,
      finalPrice: item.final_price,
      originalLinePrice: item.original_line_price,
      finalLinePrice: item.final_line_price,
      discounts: item.discounts || [],
      discountAllocations: item.line_level_discount_allocations || []
    })).sort((a, b) => a.key.localeCompare(b.key));
    return JSON.stringify(stableValue({
      token: cart.token, currency: cart.currency, count: cart.item_count,
      total: cart.total_price, originalTotal: cart.original_total_price,
      totalDiscount: cart.total_discount,
      discounts: cart.cart_level_discount_applications || [],
      note: cart.note || '', attributes: cart.attributes || {}, items
    }));
  }
  function usableReceipt(value) {
    return !!value && value.version === 1 && typeof value.selection === 'string' && typeof value.cart === 'string';
  }
  function readBuyNowReceipt() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(buyNowReceiptKey) || 'null');
      if (usableReceipt(saved)) { buyNowReceiptMemory = saved; return saved; }
    } catch (_) { /* Private browsing can deny storage; this page still has memory. */ }
    return usableReceipt(buyNowReceiptMemory) ? buyNowReceiptMemory : null;
  }
  function storeBuyNowReceipt(value) {
    buyNowReceiptMemory = usableReceipt(value) ? value : null;
    try {
      if (buyNowReceiptMemory) window.sessionStorage.setItem(buyNowReceiptKey, JSON.stringify(buyNowReceiptMemory));
      else window.sessionStorage.removeItem(buyNowReceiptKey);
    } catch (_) { /* The in-memory receipt still covers a back/forward-cache return. */ }
  }
  function canResumeBuyNow(receipt, selection, cart) {
    if (!usableReceipt(receipt) || !selection || receipt.selection !== selection) return false;
    const signature = cartSignature(cart);
    return !!signature && receipt.cart === signature;
  }
  // END BUY-NOW RECEIPT HELPERS

  const buyNowStates = new WeakMap();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-nuitigo-buy-now')) return;
    const action = new URL(form.action, window.location.origin);
    const destination = new URL(form.dataset.checkoutUrl || '/checkout', window.location.origin);
    if (action.origin !== window.location.origin || destination.origin !== window.location.origin || !/\/cart\/add\/?$/.test(action.pathname)) return;
    event.preventDefault();
    if (buyNowStates.has(form) || !form.reportValidity()) return;

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (!submitter || submitter.disabled) return;
    const data = new FormData(form);
    const fields = Array.from(form.querySelectorAll('button, input:not([type="hidden"]), select'));
    const state = { fields: fields.map((field) => [field, field.disabled]), submitter, label: submitter.innerHTML };
    buyNowStates.set(form, state);
    fields.forEach((field) => { field.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    submitter.textContent = 'Ouverture du paiement…';
    const errorBox = form.querySelector('[data-product-error]');
    if (errorBox) { errorBox.replaceChildren(); errorBox.hidden = true; }
    data.delete('return_to');
    const endpoint = action.pathname.replace(/\/$/, '') + '.js';
    const cartEndpoint = action.pathname.replace(/\/add\/?$/, '.js');
    const selection = selectionSignature(data);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    let failureMessage = 'La connexion ne permet pas de confirmer votre sélection.';
    async function readCurrentCart() {
      const response = await fetch(cartEndpoint, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Panier non confirmé.');
      const current = await response.json();
      if (!current || !Array.isArray(current.items)) throw new Error('Panier incomplet.');
      return current;
    }
    try {
      const receipt = readBuyNowReceipt();
      if (selection && receipt && receipt.selection === selection) {
        // An identical form is a checkout resumption only while the full cart is unchanged.
        // Failed verification must never cause a second, potentially duplicate POST.
        const current = await readCurrentCart();
        if (canResumeBuyNow(receipt, selection, current)) {
          window.location.assign(destination.pathname + destination.search);
          return;
        }
      }
      storeBuyNowReceipt(null);
      const response = await fetch(endpoint, {
        method: 'POST', body: data, credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (!response.ok) {
        const explanation = result.description || result.message;
        failureMessage = typeof explanation === 'string' ? explanation : 'Cet article ne peut pas être ajouté pour le moment.';
        throw new Error(failureMessage);
      }
      // A redirect is safe only after Shopify confirms the selected line item.
      const added = Array.isArray(result.items) ? result.items : [result];
      if (!added.some((line) => String(line.variant_id || line.id) === String(data.get('id')) && Number(line.quantity) > 0)) {
        failureMessage = 'La réponse reçue ne confirme pas votre sélection.';
        throw new Error(failureMessage);
      }
      // Read the post-add cart so a later retry can prove it is the same purchase.
      // If this read fails, send the customer to the cart rather than adding again.
      failureMessage = 'Votre sélection a été ajoutée, mais son récapitulatif ne peut pas être confirmé.';
      const confirmedCart = await readCurrentCart();
      const signature = cartSignature(confirmedCart);
      const addedKeys = added.map((line) => line.key).filter((key) => typeof key === 'string');
      if (!signature || !addedKeys.length || !addedKeys.every((key) => confirmedCart.items.some((line) => line.key === key))) {
        throw new Error('Le panier ne confirme pas la sélection ajoutée.');
      }
      if (selection) storeBuyNowReceipt({ version: 1, selection, cart: signature });
      window.location.assign(destination.pathname + destination.search);
    } catch (error) {
      // Never silently retry a POST: a lost response or stock error can follow
      // a successful/partial add. Verify the full cart before adding again.
      form.removeAttribute('aria-busy');
      submitter.textContent = 'Confirmation interrompue';
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.append(document.createTextNode(failureMessage + ' Vérifiez votre panier avant de réessayer. '));
        const link = document.createElement('a');
        link.href = action.pathname.replace(/\/add\/?$/, '');
        link.textContent = 'Vérifier mon panier';
        link.className = 'n-cart-recovery';
        errorBox.append(link);
        errorBox.tabIndex = -1;
        errorBox.focus();
      }
    } finally {
      window.clearTimeout(timeout);
    }
  });

  // Returning from checkout restores controls without repeating the add.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-nuitigo-buy-now]').forEach((form) => {
      const state = buyNowStates.get(form);
      if (!state) return;
      state.fields.forEach(([field, disabled]) => { field.disabled = disabled; });
      state.submitter.innerHTML = state.label;
      const picker = form.querySelector('[data-variant-picker]');
      const selected = picker && picker.selectedOptions[0];
      if (selected) {
        state.submitter.disabled = selected.dataset.available !== 'true';
        if (state.submitter.disabled) state.submitter.textContent = 'Indisponible';
      }
      const errorBox = form.querySelector('[data-product-error]');
      if (errorBox) { errorBox.replaceChildren(); errorBox.hidden = true; }
      form.removeAttribute('aria-busy');
      buyNowStates.delete(form);
    });
  });

  const dialog = document.querySelector('[data-nuitigo-cart]');
  if (!dialog || dialog.dataset.initialized === 'true') return;
  const cartPath = new URL(dialog.dataset.cartUrl, window.location.origin).pathname.replace(/\/+$/, '');
  const onCartPage = window.location.pathname.replace(/\/+$/, '') === cartPath;
  // The full cart uses a server-rendered quantity form; do not let a drawer make it stale.
  if (onCartPage || typeof dialog.showModal !== 'function') {
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-cart-open]')) return;
      event.preventDefault();
      window.location.assign(dialog.dataset.cartUrl);
    });
    return;
  }
  dialog.dataset.initialized = 'true';

  const items = dialog.querySelector('[data-cart-items]');
  const body = dialog.querySelector('[data-cart-body]');
  const empty = dialog.querySelector('[data-cart-empty]');
  const total = dialog.querySelector('[data-cart-total]');
  const message = dialog.querySelector('[data-cart-message]');
  const checkout = dialog.querySelector('[data-cart-checkout]');
  let cart = null;
  let cartVerified = false;
  let busy = false;
  let queue = Promise.resolve();
  let returnFocus = null;
  let previousOverflow = '';

  function announce(text, error = false) {
    message.textContent = text;
    message.dataset.error = String(error);
  }

  function setBusy(value) {
    busy = value;
    body.setAttribute('aria-busy', String(value));
    items.querySelectorAll('button, input').forEach((node) => { node.disabled = value || !cartVerified; });
    checkout.disabled = value || !cartVerified || !cart || cart.item_count === 0;
  }

  function enqueue(task) {
    queue = queue.then(async () => {
      cartVerified = false;
      setBusy(true);
      try { await task(); }
      catch (error) {
        const reason = error.message || 'Le panier ne peut pas être mis à jour.';
        // A failed request may still have changed the server cart. Read its actual state once.
        try {
          render(await request(dialog.dataset.jsonUrl));
          announce(reason + ' Le panier affiché a été vérifié.', true);
        } catch (_) {
          // Restore the last known quantities, but keep purchase and edits locked until verified.
          if (cart) render(cart, false);
          cartVerified = false;
          announce('Impossible de confirmer le panier. Consultez le panier complet avant de poursuivre ou de réessayer.', true);
        }
      }
      finally { setBusy(false); }
    });
    return queue;
  }

  async function request(url, options = {}) {
    let response;
    try {
      response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } });
    } catch (_) {
      throw new Error('Connexion interrompue : impossible de confirmer la mise à jour. Vérifiez le panier complet avant de réessayer.');
    }
    let data;
    try { data = await response.json(); }
    catch (_) { throw new Error('La réponse du panier est indisponible. Vérifiez le panier complet avant de réessayer.'); }
    if (!response.ok) {
      const description = data.description || data.message;
      throw new Error(typeof description === 'string' ? description : 'La quantité demandée ne peut pas être ajoutée. Vérifiez votre panier.');
    }
    return data;
  }

  function money(cents) {
    const currency = (cart && cart.currency) || dialog.dataset.currency || 'EUR';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return (cents / 100).toFixed(2) + ' ' + currency; }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function productUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search : dialog.dataset.cartUrl;
    } catch (_) { return dialog.dataset.cartUrl; }
  }

  function control(label, text, key, action) {
    const button = element('button', '', text);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.dataset.cartKey = key;
    button.dataset.cartAction = action;
    return button;
  }

  function render(nextCart, verified = true) {
    if (!nextCart || !Array.isArray(nextCart.items)) throw new Error('Le panier reçu est incomplet. Consultez le panier complet.');
    const focused = document.activeElement;
    const focusedKey = focused && focused.dataset.cartKey;
    const focusedAction = focused && focused.dataset.cartAction;
    cart = nextCart;
    cartVerified = verified;
    const fragment = document.createDocumentFragment();
    cart.items.forEach((item) => {
      const li = element('li', 'n-cart__item');
      if (item.image) {
        const img = element('img', 'n-cart__image');
        img.src = item.image;
        img.alt = item.product_title || item.title;
        img.width = 76;
        img.height = 96;
        img.loading = 'lazy';
        li.append(img);
      } else {
        const placeholder = element('div', 'n-cart__image');
        placeholder.setAttribute('aria-hidden', 'true');
        li.append(placeholder);
      }
      const content = element('div');
      const title = element('a', 'n-cart__item-name', item.product_title || item.title);
      title.href = productUrl(item.url);
      content.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') content.append(element('p', 'n-cart__details', item.variant_title));
      if (item.selling_plan_allocation) content.append(element('p', 'n-cart__details', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        const readableFormat = {'130 × 200 cm': 'largeur 130 cm, hauteur 200 cm', '152 × 180 cm': 'largeur 152 cm, hauteur 180 cm'};
        const displayValue = key === 'Format' ? (readableFormat[value] || value) : value;
        if (!key.startsWith('_') && value !== '' && value !== null) content.append(element('p', 'n-cart__details', key + ' : ' + displayValue));
      });
      content.append(element('p', 'n-cart__price', money(item.final_line_price)));
      const controls = element('div', 'n-cart__controls');
      const quantity = element('div', 'n-cart__quantity');
      quantity.append(control('Diminuer la quantité de ' + item.product_title, '−', item.key, 'decrease'));
      const input = element('input');
      input.type = 'number';
      input.min = '0';
      input.step = '1';
      input.inputMode = 'numeric';
      input.value = String(item.quantity);
      input.setAttribute('aria-label', 'Quantité de ' + item.product_title);
      input.dataset.cartKey = item.key;
      input.dataset.cartAction = 'quantity';
      quantity.append(input, control('Augmenter la quantité de ' + item.product_title, '+', item.key, 'increase'));
      const remove = control('Supprimer ' + item.product_title, 'Supprimer', item.key, 'remove');
      remove.className = 'n-cart__remove';
      controls.append(quantity, remove);
      content.append(controls);
      li.append(content);
      fragment.append(li);
    });
    items.replaceChildren(fragment);
    empty.hidden = cart.item_count !== 0;
    total.textContent = money(cart.total_price);
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
      node.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-open]').forEach((node) => {
      node.setAttribute('aria-label', 'Panier, ' + cart.item_count + ' article' + (cart.item_count === 1 ? '' : 's'));
    });
    setBusy(busy);
    if (focusedKey && dialog.open) {
      const match = Array.from(items.querySelectorAll('[data-cart-key]')).find((node) => node.dataset.cartKey === focusedKey && node.dataset.cartAction === focusedAction);
      // Inputs remain disabled until the serialized operation has completed.
      requestAnimationFrame(() => {
        const target = match || items.querySelector('input') || dialog.querySelector('[data-cart-close]');
        if (dialog.open && !target.disabled) target.focus({ preventScroll: true });
      });
    }
    if (verified) document.dispatchEvent(new CustomEvent('nuitigo:cart-updated', { detail: { cart } }));
  }

  // A browser back navigation may restore the header from before checkout.
  // Read Shopify's current cart without adding items or opening the drawer.
  window.addEventListener('pageshow', () => {
    enqueue(async () => { render(await request(dialog.dataset.jsonUrl)); });
  });

  function open(opener) {
    if (dialog.open) return;
    returnFocus = opener instanceof HTMLElement ? opener : document.activeElement;
    previousOverflow = document.documentElement.style.overflow;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  dialog.addEventListener('close', () => {
    document.documentElement.style.overflow = previousOverflow;
    const target = returnFocus && returnFocus.isConnected && !returnFocus.disabled ? returnFocus : document.querySelector('[data-cart-open]');
    if (target) target.focus({ preventScroll: true });
  });

  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) { dialog.close(); return; }
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (!opener) return;
    event.preventDefault();
    if (dialog.open) return;
    open(opener);
    announce('Chargement du panier…');
    enqueue(async () => { render(await request(dialog.dataset.jsonUrl)); announce(''); });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-nuitigo-buy-now')) return;
    if (form === checkout.form && (busy || !cartVerified)) {
      event.preventDefault();
      announce('Vérifiez le panier complet avant de poursuivre le paiement.', true);
      return;
    }
    const url = new URL(form.action, window.location.origin);
    if (url.origin !== window.location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return;
    event.preventDefault();
    if (form.dataset.cartSubmitting === 'true') return;
    const data = new FormData(form);
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    const wasDisabled = submitter && submitter.disabled;
    const variantPicker = form.querySelector('[data-variant-picker]');
    const variantWasDisabled = variantPicker && variantPicker.disabled;
    form.dataset.cartSubmitting = 'true';
    if (submitter) submitter.disabled = true;
    if (variantPicker) variantPicker.disabled = true;
    enqueue(async () => {
      try {
        await request(dialog.dataset.addUrl, { method: 'POST', body: data });
        open(submitter);
        announce('Article ajouté. Mise à jour du panier…');
        try { render(await request(dialog.dataset.jsonUrl)); announce('Article ajouté au panier.'); }
        catch (_) { throw new Error('Article ajouté, mais le panier ne peut pas être affiché. Consultez le panier complet avant un nouvel ajout.'); }
      } catch (error) {
        open(submitter);
        throw error;
      }
    }).finally(() => {
      delete form.dataset.cartSubmitting;
      if (variantPicker) variantPicker.disabled = variantWasDisabled;
      const selected = variantPicker && variantPicker.selectedOptions[0];
      if (submitter) submitter.disabled = selected ? selected.dataset.available !== 'true' : wasDisabled;
    });
  });

  function changeQuantity(key, quantity) {
    enqueue(async () => {
      announce('Mise à jour du panier…');
      const next = await request(dialog.dataset.changeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      render(next);
      const line = next.items.find((item) => item.key === key);
      announce(quantity === 0 ? 'Article supprimé du panier.' : line && line.quantity !== quantity ? 'La quantité disponible a été ajustée à ' + line.quantity + '.' : 'Quantité mise à jour.');
    });
  }

  items.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-cart-action]');
    if (!button || busy || !cartVerified || !cart) return;
    const item = cart.items.find((line) => line.key === button.dataset.cartKey);
    if (!item) return;
    const action = button.dataset.cartAction;
    const quantity = action === 'remove' ? 0 : Math.max(0, item.quantity + (action === 'increase' ? 1 : -1));
    changeQuantity(item.key, quantity);
  });

  items.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-cart-key]');
    if (!input || busy || !cartVerified || !cart) return;
    const quantity = Number(input.value);
    const item = cart.items.find((line) => line.key === input.dataset.cartKey);
    if (!item) return;
    if (!Number.isSafeInteger(quantity) || quantity < 0 || input.value.trim() === '') {
      input.value = String(item.quantity);
      announce('Saisissez une quantité entière égale ou supérieure à zéro.', true);
      return;
    }
    if (quantity !== item.quantity) changeQuantity(item.key, quantity);
  });
})();
